import { useState, useRef, useEffect, useCallback } from 'react';
import type { BlockNoteEditor } from '@blocknote/core';
import { AgentPicker } from './AgentPicker';
import { ChatMessage, type Message } from './ChatMessage';
import type { Agent } from './agents';
import { getAuthHeaders } from '../../auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8788';

interface ChatPanelProps {
  mode: 'sidebar' | 'modal';
  editor: BlockNoteEditor | null;
  onClose: () => void;
  /** When set, auto-select this agent on mount (e.g. from /verhaal slash cmd). */
  initialAgent?: Agent | null;
}

export function ChatPanel({ mode, editor, onClose, initialAgent }: ChatPanelProps) {
  const [agent, setAgent] = useState<Agent | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [connected, setConnected] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when agent is selected
  useEffect(() => {
    if (agent) {
      inputRef.current?.focus();
    }
  }, [agent]);

  // Auto-select agent when opened via slash command (e.g. /verhaal)
  useEffect(() => {
    if (initialAgent && !agent) {
      // Defer to handleSelectAgent once it's defined (via the callback below)
      const timer = setTimeout(() => {
        handleSelectAgent(initialAgent);
      }, 0);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAgent]);

  const handleSelectAgent = useCallback(async (selected: Agent) => {
    setAgent(selected);
    setMessages([]);
    setSystemPrompt('');
    setConnected(false);

    // Try to fetch SOUL.md from R2
    try {
      const res = await fetch(`${API_URL}/v1/assets/soul/${selected.id}`);
      if (res.ok) {
        const data = await res.json();
        setSystemPrompt(data.content);
      } else {
        // Fallback to agent description
        setSystemPrompt(`You are ${selected.name} (${selected.emoji}), ${selected.description}. Help the user with their document.`);
      }
    } catch {
      setSystemPrompt(`You are ${selected.name} (${selected.emoji}), ${selected.description}. Help the user with their document.`);
    }

    // Fetch recent SK beats for context (uses same bb_ key)
    try {
      const beatsRes = await fetch(
        `https://sk-mcp.bebond.net/mcp`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'tools/call',
            id: 1,
            params: {
              name: 'beat_query',
              arguments: {
                agent: selected.id.toLowerCase(),
                limit: 5,
              },
            },
          }),
        }
      );
      if (beatsRes.ok) {
        // Beats fetched -- context available for system prompt enrichment
        // The beats data can be prepended to system prompt if needed
      }
    } catch {
      // SK beats are optional context, continue without them
    }

    setConnected(true);
  }, []);

  const handleSend = async () => {
    if (!input.trim() || !agent || loading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    // Build messages array for API
    const apiMessages = newMessages.map((m) => ({
      role: m.role === 'agent' ? 'assistant' : 'user',
      content: m.content,
    }));

    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: agent.id,
          messages: apiMessages,
          systemPrompt,
        }),
      });

      if (!res.ok) {
        throw new Error(`Chat request failed: ${res.status}`);
      }

      // Handle streaming response
      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response body');

      const agentMessage: Message = {
        id: crypto.randomUUID(),
        role: 'agent',
        content: '',
        agentEmoji: agent.emoji,
      };

      setMessages((prev) => [...prev, agentMessage]);

      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        // Parse SSE data lines
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            try {
              const parsed = JSON.parse(data);
              const token = parsed.response || parsed.content || '';
              fullContent += token;
              // Update the agent message content
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === agentMessage.id
                    ? { ...m, content: fullContent }
                    : m
                )
              );
            } catch {
              // Not JSON, treat as raw text token
              fullContent += data;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === agentMessage.id
                    ? { ...m, content: fullContent }
                    : m
                )
              );
            }
          }
        }
      }
    } catch (err) {
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: 'agent',
        content: `Error: ${err instanceof Error ? err.message : 'Failed to get response'}`,
        agentEmoji: agent.emoji,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      // Fallback: select text
    }
  };

  const handleInsert = (content: string) => {
    if (!editor) return;
    try {
      // Get current cursor position and insert a new paragraph block with the content
      const currentBlock = editor.getTextCursorPosition().block;
      editor.insertBlocks(
        [{ type: 'paragraph', content: content }],
        currentBlock,
        'after'
      );
    } catch {
      // Fallback: insert at end
      try {
        const lastBlock = editor.document[editor.document.length - 1];
        editor.insertBlocks(
          [{ type: 'paragraph', content: content }],
          lastBlock,
          'after'
        );
      } catch {
        // Editor might not be available
      }
    }
  };

  const handleBack = () => {
    setAgent(null);
    setMessages([]);
    setSystemPrompt('');
    setConnected(false);
  };

  const panelContent = (
    <div className="chat-panel-inner">
      <div className="chat-panel-header">
        {agent ? (
          <>
            <button className="chat-back-btn" onClick={handleBack} title="Back to agents">
              &larr;
            </button>
            <span className="chat-agent-emoji">{agent.emoji}</span>
            <span className="chat-agent-name">{agent.name}</span>
            <span className={`chat-status-dot ${connected ? 'connected' : 'disconnected'}`} />
          </>
        ) : (
          <span className="chat-panel-title">Agent Chat</span>
        )}
        <button className="chat-close-btn" onClick={onClose} title="Close">
          &times;
        </button>
      </div>

      <div className="chat-panel-body">
        {!agent ? (
          <AgentPicker onSelect={handleSelectAgent} />
        ) : (
          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="chat-empty">
                <span className="chat-empty-emoji">{agent.emoji}</span>
                <p>Start a conversation with {agent.name}</p>
              </div>
            )}
            {messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                onCopy={handleCopy}
                onInsert={handleInsert}
              />
            ))}
            {loading && (
              <div className="chat-loading">
                <span className="chat-loading-dots">...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {agent && (
        <div className="chat-panel-footer">
          <textarea
            ref={inputRef}
            className="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${agent.name}...`}
            rows={1}
            disabled={loading}
          />
          <button
            className="chat-send-btn"
            onClick={handleSend}
            disabled={!input.trim() || loading}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );

  if (mode === 'modal') {
    return (
      <div className="chat-modal-overlay" onClick={onClose}>
        <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
          {panelContent}
        </div>
      </div>
    );
  }

  return <div className="chat-sidebar">{panelContent}</div>;
}
